import { getApp } from 'https://www.gstatic.com/firebasejs/10.14.1/firebase-app.js';
import {
  getAuth,
  onAuthStateChanged,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile,
  GoogleAuthProvider,
} from 'https://www.gstatic.com/firebasejs/10.14.1/firebase-auth.js';
import { bindResendButton, friendlyResendError } from '@studio/verify-email';
import { safeReturnUrl } from '@studio/safe-return-url';
import { registerAccount } from '@studio/signup-flow';

// ── The view module, by its VERSIONED URL ─────────────────────────────────
// Card SYKMBm5f (PR #128 bounce 3) / fy6TBJhQ. A RELATIVE import of the
// sibling module resolves to the VERSIONLESS CDN URL, which
// Shopify serves with max-age=1y — so a re-pushed view module stayed STALE on
// the served page (no DOOR_PARAM export, `?door=register` opened nothing)
// while this importer, loaded via `asset_url`, was the new file. The section
// emits the versioned URL on its root (`data-view-module`); import THAT.
// A dynamic import, not an import map: Horizon ships one and Chrome refuses a
// second ("Multiple import maps are not yet supported"). No relative fallback
// — a fallback would be the defect wearing a different hat; the section and
// this script ship in the same theme push, so a missing attribute is a real
// wiring break and must fail loud, not silently serve last year's module.
const viewModuleUrl = document.querySelector('[data-view-module]')?.dataset.viewModule;
if (!viewModuleUrl) {
  throw new Error('studio-signin: [data-view-module] is missing from the section root — the view module cannot be loaded by a versioned URL (card SYKMBm5f)');
}
const { showView, openRequestedView } = await import(viewModuleUrl);

const auth = getAuth(getApp());

// Same-origin return_url only. Card C0up7eX5 — the previous regex allow-check
// (/^\/(?![/\\])/, Gemini PR#45) rejected "//evil.com" and "/\evil.com" but ACCEPTED a
// tab/LF/CR in that slot: URLSearchParams decodes "?return_url=/%09/evil.com" to
// "/\t/evil.com", and WHATWG URL parsing then strips the C0 control, so the browser
// left the storefront. Parse, do not regex-allow. ONE helper owns the check
// (assets/studio-safe-return-url.js) and BOTH redirect sites below — the
// onAuthStateChanged replace and goToReturnUrl() — consume this single resolved value,
// so there is never a second copy of the check.
const rawReturn = new URLSearchParams(window.location.search).get('return_url') || '';
const returnUrl = safeReturnUrl(rawReturn, location.origin);

// ── View navigation ────────────────────────────────────────────────────────
// The view table and the show/hide logic live in studio-signin-view.js so a
// DOM test can exercise them without Firebase (card SYKMBm5f). `?door=register`
// / `?door=forgot` opens that view on load; anything else is the plain door.
// (`?view=` is Shopify's reserved template suffix and never reaches this code —
// see DOOR_PARAM in studio-signin-view.js.)
// NOTE: Shopify's hosted customer accounts intercept `/account/*`, so the
// register template's redirect onto `?door=register` is inert on this store
// today — see assets/studio-signin-view.js and the card.
openRequestedView(window.location.search);

// Wire [data-goto] links
document.querySelectorAll('[data-goto]').forEach((link) => {
  link.addEventListener('click', (e) => {
    e.preventDefault();
    showView(link.dataset.goto);
  });
});

// ── Auth state — redirect if already signed in ────────────────────────────

// Records how the sign-up verification email went, for the page we are about to
// redirect to. Storage is best-effort: Safari private mode throws on setItem, and a
// failure here must never break registration.
//
// Still needed AFTER uGqPRiwp's inline verify step: showVerifyStep() falls back to
// goToReturnUrl() on an older theme revision that has no [data-view="verify"], and
// on that path the destination page's notice is the ONLY thing the customer gets.
function stashVerifyOutcome(outcome) {
  try {
    sessionStorage.setItem('studio.verifyEmail', outcome);
  } catch (_e) {
    // GUARD-OK: this swallow is deliberate and cannot hide the failure it is
    // named for. sessionStorage.setItem throws in Safari private mode, and this
    // is a BREADCRUMB for the destination page, not the notification itself —
    // the caller has already console.error'd a send failure AND rendered it
    // inline via showVerifyStep(). Letting this throw would abort registration
    // for an account that has already been created.
  }
}

// ⚠️ createUserWithEmailAndPassword() signs the new user in, so this listener used
// to fire mid-registration and redirect before a "check your inbox" step could be
// read — which is why a password-provider customer reached the composer, uploaded,
// and only then hit a dead-end "Verify your email to enable uploads." with nowhere
// to verify.
//
// The note that used to sit here said any AC-1/AC-3 fix "belongs on the DESTINATION
// page ... or must gate this redirect deliberately". uGqPRiwp does the second:
// holdRedirect is the deliberate gate, released by goToReturnUrl().
let holdRedirect = false;

// Funnel events → GA4 + PostHog + Mixpanel (assets/studio-analytics.js).
// `attemptMethod` is set only by an action taken ON this page, so a visitor who
// arrives already signed in is redirected without being counted as a sign-in.
let attemptMethod = null;
function track(name, props, opts) {
  try { window.StudioAnalytics && window.StudioAnalytics.track(name, props, opts); } catch (_e) { /* GUARD-OK: analytics is best-effort telemetry; a failing sink must never block sign-in or its redirect. */ }
}
function trackAttempt(method) {
  attemptMethod = method;
  track('signin_started', { method });
}

onAuthStateChanged(auth, (user) => {
  let redirectVia = null;
  if (user && attemptMethod) {
    try { window.StudioAnalytics && window.StudioAnalytics.identify(user.uid); } catch (_e) { /* GUARD-OK: identify is best-effort telemetry; the redirect below must still run. */ }
    try { window.StudioInternal && window.StudioInternal.markIfStaff(user.email); } catch (_e) { /* GUARD-OK: staff tagging is best-effort telemetry (card t5gZei26); the redirect below must still run. */ }
    if (attemptMethod !== 'email_register' && !holdRedirect) {
      // location.replace() unloads the page synchronously, and gtag queues its
      // hit asynchronously — measured 2026-09-22, GA4 lost EVERY signin_completed
      // that way (Mixpanel/PostHog survived via their own persistence). So the
      // redirect waits for GA4's event_callback, capped by onSent's timeout.
      redirectVia = (go) => track('signin_completed', { method: attemptMethod, email_verified: !!user.emailVerified }, { beacon: true, onSent: go });
    }
  }
  if (user && !holdRedirect) {
    const go = () => window.location.replace(returnUrl);
    if (redirectVia && window.StudioAnalytics) redirectVia(go);
    else go();
  }
});

function goToReturnUrl() {
  holdRedirect = false;
  window.location.replace(returnUrl);
}

// ── Sign in ───────────────────────────────────────────────────────────────

document.querySelector('[data-google-signin]')?.addEventListener('click', async (e) => {
  const btn = e.currentTarget;
  btn.disabled = true;
  clearError('signin');
  trackAttempt('google');
  try {
    await signInWithPopup(auth, new GoogleAuthProvider());
  } catch (err) {
    track('signin_failed', { method: 'google', code: err.code || 'unknown' });
    if (err.code !== 'auth/popup-closed-by-user') showError('signin', friendly(err.code));
    btn.disabled = false;
  }
});

document.querySelector('[data-form="signin"]')?.addEventListener('submit', async (e) => {
  e.preventDefault();
  const email    = e.target.email.value.trim();
  const password = e.target.password.value;
  const btn      = e.target.querySelector('[type="submit"]');
  btn.disabled   = true;
  clearError('signin');
  trackAttempt('email');
  try {
    await signInWithEmailAndPassword(auth, email, password);
  } catch (err) {
    track('signin_failed', { method: 'email', code: err.code || 'unknown' });
    showError('signin', friendly(err.code));
    btn.disabled = false;
  }
});

// ── Register ──────────────────────────────────────────────────────────────

document.querySelector('[data-form="register"]')?.addEventListener('submit', async (e) => {
  e.preventDefault();
  const firstName = e.target.firstName.value.trim();
  const lastName  = e.target.lastName.value.trim();
  const email     = e.target.email.value.trim();
  const password  = e.target.password.value;
  const btn       = e.target.querySelector('[type="submit"]');
  btn.disabled    = true;
  clearError('register');
  holdRedirect    = true;
  trackAttempt('email_register');
  // Card kBXoBAXE: once the account exists, registration has succeeded — a lost
  // signUp response, a failed display-name write or a failed verification mail
  // must reach the verify step, never "Network error" (the account would exist
  // and a retry would answer "already exists"). See assets/studio-signup-flow.js.
  //
  // studio-backend's requireAuth enforces email_verified, so an unverified
  // password-provider customer cannot upload — the account is useless until
  // the mail lands. A send failure is therefore NOT swallowed: it is logged,
  // stashed for the destination page AND shown inline by showVerifyStep().
  const displayName = [firstName, lastName].filter(Boolean).join(' ');
  const r = await registerAccount(
    { auth, email, password, displayName },
    { createUserWithEmailAndPassword, updateProfile, sendEmailVerification },
  );
  if (!r.ok) {
    track('signin_failed', { method: 'email_register', code: r.error?.code || 'unknown' });
    holdRedirect = false;
    showError('register', friendly(r.error?.code));
    btn.disabled = false;
    return;
  }
  if (r.recovered) console.warn('[studio-signin] signUp response was lost but the account exists — continuing to the verify step');
  if (r.profileError) console.warn('[studio-signin] display name not saved:', r.profileError?.code || r.profileError);
  if (r.sendError) {
    console.error(
      '[studio-signin] verification email FAILED to send — this customer cannot upload:',
      r.sendError?.code || r.sendError,
    );
    stashVerifyOutcome('failed:' + (r.sendError?.code || 'unknown'));
  } else {
    stashVerifyOutcome('sent');
  }
  track('signup_completed', { method: 'email', verification_email: r.sendError ? 'failed' : 'sent', recovered: r.recovered });
  showVerifyStep(email, r.sendError);
});

// ── Post-signup: tell them verification is required, before they hit a 403 ──

function showVerifyStep(email, sendError) {
  const target = document.querySelector('[data-verify-email]');
  if (target) target.textContent = email;

  const failed = document.querySelector('[data-verify-send-failed]');
  if (failed) {
    failed.hidden = !sendError;
    if (sendError) failed.textContent = friendlyResendError(sendError);
  }

  // No verify view in the template (older theme revision) — do not strand the
  // customer on a disabled form: fall back to the old behaviour and redirect.
  if (!document.querySelector('[data-view="verify"]')) {
    goToReturnUrl();
    return;
  }
  showView('verify');
}

// One implementation, in studio-verify-email.js. This used to be a second copy
// of the same handler, and the two had already drifted apart — different note
// class, different copy, and a bare `30000` here against RESEND_COOLDOWN_MS
// there. Only the copy and the note class are genuinely site-specific, so they
// are options; the behaviour is not duplicated. (Bounce finding 4, uGqPRiwp.)
bindResendButton(
  document.querySelector('[data-verify-resend-signup]'),
  document.querySelector('[data-verify-resend-note]'),
  {
    errorClass: 'sa-error',
    sentText: st('resend_sent', 'Sent. Check your inbox, and your spam folder.'),
    alreadyText: st('resend_already', 'Already verified — you are good to go.'),
  },
);

document.querySelector('[data-verify-continue]')?.addEventListener('click', (e) => {
  e.preventDefault();
  goToReturnUrl();
});

// ── Forgot password ───────────────────────────────────────────────────────

document.querySelector('[data-form="forgot"]')?.addEventListener('submit', async (e) => {
  e.preventDefault();
  const email  = e.target.email.value.trim();
  const btn    = e.target.querySelector('[type="submit"]');
  btn.disabled = true;
  clearError('forgot');
  const success = document.querySelector('[data-forgot-success]');
  if (success) success.hidden = true;
  try {
    await sendPasswordResetEmail(auth, email);
    if (success) success.hidden = false;
    btn.disabled = false;
  } catch (err) {
    showError('forgot', friendly(err.code));
    btn.disabled = false;
  }
});

// ── Helpers ───────────────────────────────────────────────────────────────

function showError(view, msg) {
  const el = document.querySelector(`[data-error="${view}"]`);
  if (el) { el.textContent = msg; el.hidden = false; }
}

function clearError(view) {
  const el = document.querySelector(`[data-error="${view}"]`);
  if (el) { el.hidden = true; el.textContent = ''; }
}

// Card xYzOxcW8: the section emits window.Shopify.studioSigninStrings from the
// signin.* locale keys (this asset cannot run `| t`). A missing or empty key falls
// back to the English literal, never to a blank.
function st(key, fallback) {
  const v = globalThis.Shopify?.studioSigninStrings?.[key];
  return typeof v === 'string' && v.trim() ? v : fallback;
}

function friendly(code) {
  return {
    'auth/user-not-found':         st('err_user_not_found', 'No account found with that email.'),
    'auth/wrong-password':         st('err_wrong_password', 'Incorrect password.'),
    'auth/invalid-credential':     st('err_invalid_credential', 'Email or password is incorrect.'),
    'auth/invalid-email':          st('err_invalid_email', 'Please enter a valid email address.'),
    'auth/email-already-in-use':   st('err_email_in_use', 'An account with this email already exists.'),
    'auth/weak-password':          st('err_weak_password', 'Password must be at least 6 characters.'),
    'auth/too-many-requests':      st('err_too_many', 'Too many attempts. Wait a moment and try again.'),
    'auth/network-request-failed': st('err_network', 'Network error — check your connection.'),
  }[code] || st('err_generic', 'Something went wrong. Please try again.');
}
